import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { FiUpload, FiFile, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { toast, ToastContainer } from "react-toastify";

const ImportOMR = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [sets, setSets] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});
  const [loading, setLoading] = useState(false);

  async function fetchGenerateSets() {
    try {
      const fd = new FormData();
      fd.append("loggedInUser", localStorage.getItem("loggedInUser"));
      fd.append("token", localStorage.getItem("authToken"));
      fd.append("programType", "getPaperSets");
      fd.append("paperId", id);

      const response = await api.post("omr/omrDetails", fd);

      // console.log("✅ NO. of Generated Sets:", response.data);

      if (
        response.data.status !== "success" ||
        !response.data.sets ||
        response.data.sets.length === 0
      ) {
        toast.error("No generated sets found. Redirecting back...");

        // ✅ DELAY NAVIGATION SO TOAST IS VISIBLE
        setTimeout(() => {
          navigate("/paper-details");
        }, 2000); // 2 seconds delay

        return;
      }

      setSets(response.data.sets);

      const initialStatus = {};
      response.data.sets.forEach(set => {
        initialStatus[set.setNumber] = {
          loading: false,
          success: false,
          error: false,
          message: ''
        };
      });
      setUploadStatus(initialStatus);

    } catch (error) {
      console.error("❌ Error fetching OMR sets:", error);

      // ✅ SAME DELAY SAFETY
      setTimeout(() => {
        navigate("/paper-details");
      }, 500);
    }
  }



  const handleFileChange = (setNumber, event) => {
    const file = event.target.files[0];

    if (file) {
      // Check if file is CSV
      if (file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
        alert('Please select a CSV file only.');
        event.target.value = ''; // Clear the file input
        return;
      }

      setSelectedFiles(prev => ({
        ...prev,
        [setNumber]: file
      }));

      // Reset upload status for this set
      setUploadStatus(prev => ({
        ...prev,
        [setNumber]: {
          loading: false,
          success: false,
          error: false,
          message: ''
        }
      }));
    }
  };

  async function handleSubmit(setNumber) {
    const file = selectedFiles[setNumber];

    if (!file) {
      alert('Please select a CSV file for Set ' + setNumber);
      return;
    }

    // Update status to loading
    setUploadStatus(prev => ({
      ...prev,
      [setNumber]: {
        loading: true,
        success: false,
        error: false,
        message: 'Uploading...'
      }
    }));

    try {
      const fd = new FormData();
      fd.append("loggedInUser", localStorage.getItem("loggedInUser"));
      fd.append("token", localStorage.getItem("authToken"));
      fd.append("programType", "bulkSubmitMarks");
      fd.append("paperId", id);
      fd.append("setNumber", setNumber);
      fd.append("csvFile", file); // Add the CSV file

      const response = await api.post("examPaper/omrImport", fd);

      console.log("✅ Upload response for Set " + setNumber + ":", response.data);

      // Update status based on response
      if (response.data && response.data.status === "success") {
        setUploadStatus(prev => ({
          ...prev,
          [setNumber]: {
            loading: false,
            success: true,
            error: false,
            message: response.data.message || 'Upload successful!'
          }
        }));

        // Clear the selected file after successful upload
        setSelectedFiles(prev => {
          const newFiles = { ...prev };
          delete newFiles[setNumber];
          return newFiles;
        });
      } else {
        setUploadStatus(prev => ({
          ...prev,
          [setNumber]: {
            loading: false,
            success: false,
            error: true,
            message: response.data.message || 'Upload failed. Please try again.'
          }
        }));
      }

    } catch (error) {
      console.error("❌ Error uploading CSV for Set " + setNumber + ":", error);
      setUploadStatus(prev => ({
        ...prev,
        [setNumber]: {
          loading: false,
          success: false,
          error: true,
          message: 'Upload failed. Please check your file and try again.'
        }
      }));
    }
  }

  useEffect(() => {
    fetchGenerateSets();
  }, [id]);

  return (
    <div className="import-omr-container">
      <h2>Import OMR Data</h2>
      <p className="subtitle mb-0">Upload CSV files for each set</p>

      <p className="subtitle" style={{color:"red"}}><strong>Note: </strong>Keep 1st row empty before converting it to csv.
      </p>

      <div className="sets-grid">
        {sets.map(set => (
          <div key={set.setNumber} className="set-card">
            <div className="set-header">
              <h3>Set {set.setNumber}</h3>
              <span className="question-count">{set.questionCount} questions</span>
            </div>

            <div className="set-content">
              <div className="file-input-container">
                <label className="file-input-label">
                  <FiFile className="file-icon" />
                  <span>
                    {selectedFiles[set.setNumber]
                      ? selectedFiles[set.setNumber].name
                      : 'Select CSV file'}
                  </span>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => handleFileChange(set.setNumber, e)}
                    style={{ display: 'none' }}
                  />
                </label>

                {selectedFiles[set.setNumber] && (
                  <button
                    className="upload-btn"
                    onClick={() => handleSubmit(set.setNumber)}
                    disabled={uploadStatus[set.setNumber]?.loading}
                  >
                    {uploadStatus[set.setNumber]?.loading ? (
                      'Uploading...'
                    ) : (
                      <>
                        <FiUpload className="upload-icon" />
                        Upload
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Upload Status */}
              {uploadStatus[set.setNumber]?.message && (
                <div className={`status-message ${uploadStatus[set.setNumber]?.success ? 'success' : ''} ${uploadStatus[set.setNumber]?.error ? 'error' : ''}`}>
                  {uploadStatus[set.setNumber]?.success ? (
                    <FiCheck className="status-icon" />
                  ) : uploadStatus[set.setNumber]?.error ? (
                    <FiAlertCircle className="status-icon" />
                  ) : null}
                  <span>{uploadStatus[set.setNumber]?.message}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <ToastContainer />

      <style jsx>{`
        .import-omr-container {
          padding: 20px;
        }
        
        h2 {
          margin-bottom: 8px;
          color: #333;
        }
        
        .subtitle {
          color: #666;
          margin-bottom: 24px;
          font-size: 14px;
        }
        
        .sets-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 20px;
        }
        
        .set-card {
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 20px;
          transition: box-shadow 0.2s;
        }
        
        .set-card:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        .set-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #f0f0f0;
        }
        
        .set-header h3 {
          margin: 0;
          font-size: 18px;
          color: #333;
        }
        
        .question-count {
          background: #f0f0f0;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          color: #666;
        }
        
        .file-input-container {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .file-input-label {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          background: #f8f9fa;
          border: 2px dashed #ced4da;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .file-input-label:hover {
          background: #e9ecef;
          border-color: #adb5bd;
        }
        
        .file-icon {
          color: #6c757d;
          font-size: 18px;
        }
        
        .file-input-label span {
          flex: 1;
          color: #495057;
          font-size: 14px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .upload-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 10px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        }
        
        .upload-btn:hover:not(:disabled) {
          background: #0056b3;
        }
        
        .upload-btn:disabled {
          background: #6c757d;
          cursor: not-allowed;
        }
        
        .upload-icon {
          font-size: 16px;
        }
        
        .status-message {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px;
          border-radius: 6px;
          font-size: 13px;
          margin-top: 12px;
        }
        
        .status-message.success {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }
        
        .status-message.error {
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }
        
        .status-icon {
          font-size: 16px;
        }
        
        @media (max-width: 768px) {
          .sets-grid {
            grid-template-columns: 1fr;
          }
          
          .import-omr-container {
            padding: 15px;
          }
        }
      `}</style>
    </div>
  )
}

export default ImportOMR